import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Lock, Unlock, Trash2, X, Plus, Loader2, ShieldCheck, History, Coins, AlertTriangle, Send
} from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Governance = ({ user }) => {
  const [tab, setTab] = useState('periods');
  const [periods, setPeriods] = useState([]);
  const [audit, setAudit] = useState([]);
  const [unalloc, setUnalloc] = useState(null);
  const [overheadBalance, setOverheadBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showPeriodModal, setShowPeriodModal] = useState(false);
  const [periodForm, setPeriodForm] = useState({ name: '', start_date: '', end_date: '' });
  const [overheadForm, setOverheadForm] = useState({ period_start: '', period_end: '', method: 'revenue' });
  const [busy, setBusy] = useState(false);

  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a, u, b] = await Promise.all([
        axiosInstance.get('/periods'),
        axiosInstance.get('/audit?limit=200').catch(() => ({ data: { entries: [] } })),
        axiosInstance.get('/unallocated'),
        axiosInstance.get('/overhead/balance'),
      ]);
      setPeriods(p.data);
      setAudit(a.data?.entries || []);
      setUnalloc(u.data);
      setOverheadBalance(b.data?.balance || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const submitPeriod = async (e) => {
    e.preventDefault();
    if (!periodForm.name || !periodForm.start_date || !periodForm.end_date) return toast.error('All fields required');
    setBusy(true);
    try {
      await axiosInstance.post('/periods', periodForm);
      toast.success('Period created');
      setShowPeriodModal(false); setPeriodForm({ name: '', start_date: '', end_date: '' });
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'فشل'); }
    finally { setBusy(false); }
  };

  const closePeriod = async (id) => {
    if (!window.confirm('قفل الفترة؟ سيتم منع أي قيود جديدة في تواريخها.')) return;
    try { await axiosInstance.post(`/periods/${id}/close`); toast.success('Period closed'); fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'فشل'); }
  };
  const reopenPeriod = async (id) => {
    if (!window.confirm('فتح الفترة المُقفلة؟')) return;
    try { await axiosInstance.post(`/periods/${id}/reopen`); toast.success('Period reopened'); fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'فشل'); }
  };
  const deletePeriod = async (id) => {
    if (!window.confirm('حذف الفترة؟')) return;
    try { await axiosInstance.delete(`/periods/${id}`); toast.success('Period deleted'); fetchAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'فشل'); }
  };
  const distributeOverhead = async () => {
    if (!overheadForm.period_start || !overheadForm.period_end) return toast.error('Date range required');
    if (!window.confirm('توزيع المصاريف الإدارية على المشاريع؟ سيتم إنشاء قيد محاسبي.')) return;
    setBusy(true);
    try {
      const params = new URLSearchParams(overheadForm);
      const res = await axiosInstance.post(`/overhead/distribute?${params}`);
      toast.success(`Distributed ${fmt(res.data.total)} across ${res.data.distributions?.length || 0} projects (${res.data.journal_no || ''})`);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'فشل'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="governance-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-rose-600" /> Governance & Audit
          </h1>
          <p className="text-sm text-gray-500 mt-1">إقفال الفترات، سجل التدقيق، توزيع المصاريف الإدارية، المصاريف غير المخصصة</p>
        </div>
      </div>

      <div className="flex gap-2 border-b border-gray-200 flex-wrap">
        {[
          { k: 'periods', label: 'Periods / إقفال الفترات', icon: Lock },
          { k: 'audit', label: 'Audit Log / سجل التدقيق', icon: History },
          { k: 'overhead', label: 'Overhead Pool / توزيع الإداري', icon: Coins },
          { k: 'unallocated', label: 'Unallocated / مصاريف بلا مشروع', icon: AlertTriangle },
        ].map(({ k, label, icon: Icon }) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px ${
              tab === k ? 'border-rose-600 text-rose-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {loading && <div className="p-10 text-center text-gray-500 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>}

      {!loading && tab === 'periods' && (
        <div className="space-y-3">
          {isAdmin && (
            <button data-testid="new-period-btn" onClick={() => setShowPeriodModal(true)}
              className="px-4 py-2 rounded-lg bg-rose-600 text-white text-sm hover:bg-rose-700 flex items-center gap-2">
              <Plus className="w-4 h-4" /> New Period
            </button>
          )}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            {periods.length === 0 ? <div className="p-10 text-center text-gray-400">لا توجد فترات بعد.</div> : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500">Name</th>
                    <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500">Start</th>
                    <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500">End</th>
                    <th className="px-3 py-2 text-center text-[11px] uppercase text-gray-500">Status</th>
                    <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500">Closed By</th>
                    <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {periods.map((p) => (
                    <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2 text-sm font-medium">{p.name}</td>
                      <td className="px-3 py-2 text-sm font-mono">{p.start_date}</td>
                      <td className="px-3 py-2 text-sm font-mono">{p.end_date}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded ${p.status === 'closed' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                          {p.status === 'closed' ? '🔒 Closed' : '🔓 Open'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600">{p.closed_by_name || '—'}</td>
                      <td className="px-3 py-2 text-right">
                        {isAdmin && (
                          <div className="flex justify-end gap-1">
                            {p.status === 'open' ? (
                              <button data-testid={`close-period-${p.id}`} onClick={() => closePeriod(p.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" title="Close"><Lock className="w-4 h-4" /></button>
                            ) : (
                              <button data-testid={`reopen-period-${p.id}`} onClick={() => reopenPeriod(p.id)} className="p-1.5 hover:bg-emerald-50 rounded text-emerald-600" title="Reopen"><Unlock className="w-4 h-4" /></button>
                            )}
                            {p.status === 'open' && (
                              <button onClick={() => deletePeriod(p.id)} className="p-1.5 hover:bg-gray-100 rounded text-red-600"><Trash2 className="w-4 h-4" /></button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {!loading && tab === 'audit' && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="audit-list">
          {audit.length === 0 ? <div className="p-10 text-center text-gray-400">No audit entries yet.</div> : (
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500">Time</th>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500">User</th>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500">Action</th>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500">Entity</th>
                  <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500">Reference</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((e) => (
                  <tr key={e.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 text-xs font-mono text-gray-500">{(e.ts || '').slice(0, 19).replace('T', ' ')}</td>
                    <td className="px-3 py-2 text-sm">{e.user_name}</td>
                    <td className="px-3 py-2 text-sm">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        e.action === 'create' ? 'bg-emerald-100 text-emerald-700' :
                        e.action === 'delete' ? 'bg-red-100 text-red-700' :
                        e.action === 'close' || e.action === 'reverse' ? 'bg-amber-100 text-amber-700' :
                        e.action === 'post' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>{e.action}</span>
                    </td>
                    <td className="px-3 py-2 text-sm capitalize">{e.entity_type}</td>
                    <td className="px-3 py-2 text-xs font-mono text-gray-500">
                      {e.after?.journal_no || e.after?.reference || e.metadata?.reference_id || e.entity_id?.slice(0, 8)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!loading && tab === 'overhead' && (
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-5">
            <div className="text-xs text-gray-600 mb-1">Current Overhead Balance (52xxx admin accounts)</div>
            <div className="text-3xl font-bold font-mono text-amber-700">{fmt(overheadBalance)} SAR</div>
            <div className="text-xs text-gray-500 mt-1">Total admin expenses not yet allocated to a specific project.</div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3" data-testid="overhead-form">
            <div className="text-sm font-semibold text-gray-700">Distribute admin expenses across active projects</div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Period Start *</label>
                <input type="date" value={overheadForm.period_start} onChange={(e) => setOverheadForm({ ...overheadForm, period_start: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Period End *</label>
                <input type="date" value={overheadForm.period_end} onChange={(e) => setOverheadForm({ ...overheadForm, period_end: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Method</label>
                <select value={overheadForm.method} onChange={(e) => setOverheadForm({ ...overheadForm, method: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm">
                  <option value="revenue">Weighted by Revenue</option>
                  <option value="equal">Equal Split</option>
                </select>
              </div>
            </div>
            {isAdmin && (
              <button data-testid="distribute-overhead-btn" onClick={distributeOverhead} disabled={busy}
                className="px-4 py-1.5 bg-amber-600 text-white rounded text-sm hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Distribute
              </button>
            )}
            <div className="text-xs text-gray-500">
              📌 سينشئ قيداً يُعيد تصنيف المصاريف الإدارية إلى تكلفة المشاريع بحسب الطريقة المختارة.
            </div>
          </div>
        </div>
      )}

      {!loading && tab === 'unallocated' && unalloc && (
        <div className="space-y-4" data-testid="unallocated-section">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-xs text-gray-600 mb-1">Expenses without project</div>
              <div className="text-2xl font-bold font-mono text-red-700">{unalloc.count_expenses}</div>
              <div className="text-sm text-red-600">{fmt(unalloc.total_expense_value)} SAR</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="text-xs text-gray-600 mb-1">Journal lines (cost) without project</div>
              <div className="text-2xl font-bold font-mono text-amber-700">{unalloc.journal_lines_unallocated}</div>
              <div className="text-sm text-amber-600">{fmt(unalloc.journal_lines_total)} SAR</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-xs text-gray-600 mb-1">Recommendation</div>
              <div className="text-sm text-blue-700">Open these expenses and assign a project, or create a Cost Allocation Rule so they auto-route.</div>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 border-b text-xs font-semibold text-gray-700">Top unallocated expenses (max 200)</div>
            {unalloc.expenses?.length === 0 ? <div className="p-6 text-center text-emerald-600">✓ كل المصاريف مخصصة لمشاريع.</div> : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500">#</th>
                    <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500">Date</th>
                    <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500">Type</th>
                    <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500">Description</th>
                    <th className="px-3 py-2 text-right text-[11px] uppercase text-gray-500">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {unalloc.expenses?.map((e) => (
                    <tr key={e.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2 text-xs font-mono">{e.expense_no}</td>
                      <td className="px-3 py-2 text-sm">{e.expense_date}</td>
                      <td className="px-3 py-2 text-sm">{e.expense_type}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">{e.description}</td>
                      <td className="px-3 py-2 text-sm text-right font-mono">{fmt(e.total_with_vat)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Period Modal */}
      {showPeriodModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowPeriodModal(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-xl max-w-md w-full" data-testid="period-modal">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h3 className="font-semibold">New Accounting Period</h3>
              <button onClick={() => setShowPeriodModal(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submitPeriod} className="px-5 py-4 space-y-3">
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Name *</label>
                <input data-testid="period-name" required value={periodForm.name} onChange={(e) => setPeriodForm({ ...periodForm, name: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" placeholder="e.g. February 2026" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Start *</label>
                  <input type="date" required value={periodForm.start_date} onChange={(e) => setPeriodForm({ ...periodForm, start_date: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">End *</label>
                  <input type="date" required value={periodForm.end_date} onChange={(e) => setPeriodForm({ ...periodForm, end_date: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowPeriodModal(false)} className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50">Cancel</button>
                <button type="submit" data-testid="period-submit" disabled={busy} className="px-4 py-1.5 text-sm bg-rose-600 text-white rounded hover:bg-rose-700 disabled:opacity-50">{busy ? 'Saving…' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Governance;
